'use client'

import { useEffect, useRef } from 'react'
import { useAuth } from '@/contexts/auth-context'
import { hasFunctionalConsent } from '@/lib/cookie-consent'

// The website token is a public widget identifier (designed to be embedded in
// page HTML), not a secret — safe to ship as a default. Both values can still
// be overridden per-environment via NEXT_PUBLIC_* without a code change.
const BASE_URL = process.env.NEXT_PUBLIC_CHATWOOT_BASE_URL ?? 'https://crm.extenshi.io'
const WEBSITE_TOKEN = process.env.NEXT_PUBLIC_CHATWOOT_WEBSITE_TOKEN ?? '6NW8JZbHxyTtL1V4EBBANWV3'
const SCRIPT_ID = 'chatwoot-sdk'

interface ChatwootSDK {
	run: (opts: { websiteToken: string; baseUrl: string }) => void
}
interface ChatwootApi {
	reset: () => void
	setUser: (identifier: string, attrs: { email?: string; name?: string; identifier_hash?: string }) => void
	toggleBubbleVisibility: (visibility: 'show' | 'hide') => void
}
declare global {
	interface Window {
		chatwootSDK?: ChatwootSDK
		chatwootSettings?: Record<string, unknown>
		$chatwoot?: ChatwootApi
	}
}

/**
 * Loads the Chatwoot live-chat widget, but only for authenticated users who
 * have granted *functional* cookie consent — the SDK sets first-party cookies
 * on crm.extenshi.io, so it falls under the "functional" category the consent
 * banner advertises as "Live chat support" (see `lib/cookie-consent.ts`).
 *
 * Mirrors the analytics-consent gate around PostHog in `providers.tsx`:
 * re-evaluates on the `cookie-consent-updated` event and tears the session
 * down (reset + hide) when auth ends or consent is withdrawn mid-session, so
 * withdrawal is as effective as never having loaded it.
 *
 * Renders nothing; the widget mounts itself into <body>.
 */
export function ChatwootWidget() {
	const { user, isAuthenticated } = useAuth()
	// Read the latest user inside callbacks without re-subscribing the SDK
	// listeners on every background token refresh (which produces a new `user`
	// object with the same id).
	const userRef = useRef(user)
	userRef.current = user

	useEffect(() => {
		function injectScript() {
			if (window.chatwootSDK || document.getElementById(SCRIPT_ID)) return
			window.chatwootSettings = { position: 'right', type: 'standard', launcherTitle: '' }
			const script = document.createElement('script')
			script.id = SCRIPT_ID
			script.src = `${BASE_URL}/packs/js/sdk.js`
			script.async = true
			script.addEventListener('load', () => {
				window.chatwootSDK?.run({ websiteToken: WEBSITE_TOKEN, baseUrl: BASE_URL })
			})
			document.body.appendChild(script)
		}

		// Identify the logged-in user to Chatwoot with a server-signed
		// `identifier_hash` so the inbox's identity-validation (HMAC) accepts it
		// and nobody can impersonate this user with the public website token.
		// Best-effort: any failure leaves the widget working anonymously rather
		// than throwing — `/api/chatwoot/identity` returns 503 before the secret
		// is provisioned and 401 when the session can't be verified.
		async function identify() {
			const current = userRef.current
			if (!current || !window.$chatwoot) return
			try {
				const res = await fetch('/api/chatwoot/identity', {
					credentials: 'include',
					headers: { accept: 'application/json' },
				})
				if (!res.ok) return
				const { identifier, identifierHash } = (await res.json()) as {
					identifier?: string
					identifierHash?: string
				}
				if (!identifier || !identifierHash) return
				// Auth may have ended (logout / token rotation) during the await.
				if (!userRef.current || !window.$chatwoot) return
				window.$chatwoot.setUser(identifier, {
					email: current.email,
					name: current.email,
					identifier_hash: identifierHash,
				})
			} catch {
				// Network/parse failure — stay anonymous, do not break the widget.
			}
		}

		// Single source of truth for the desired widget state.
		function sync() {
			const shouldShow = isAuthenticated && hasFunctionalConsent()
			if (shouldShow) {
				injectScript()
				// `$chatwoot` only exists once the SDK has booted; when it isn't ready
				// yet the `chatwoot:ready` listener below re-runs sync() on boot.
				if (window.$chatwoot) {
					void identify()
					window.$chatwoot.toggleBubbleVisibility('show')
				}
			} else if (window.$chatwoot) {
				window.$chatwoot.reset()
				window.$chatwoot.toggleBubbleVisibility('hide')
			}
		}

		window.addEventListener('chatwoot:ready', sync)
		window.addEventListener('cookie-consent-updated', sync)
		sync()

		return () => {
			window.removeEventListener('chatwoot:ready', sync)
			window.removeEventListener('cookie-consent-updated', sync)
		}
	}, [isAuthenticated])

	return null
}
